import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const passwordFormSchema = z.object({
  currentPassword: z.string().min(6, { message: 'Current password is required' }),
  newPassword: z.string().min(6, { message: 'New password is required' }),
  confirmPassword: z.string().min(6, { message: 'Please confirm your new password' }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

const PractitionerPasswordForm: React.FC = () => {
  const { t } = useTranslation('settings');
  const { updatePassword } = useAuth();
  const { toast } = useToast();
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (values: PasswordFormValues) => {
    try {
      await updatePassword(values.newPassword);
      toast({ title: t('security.passwordUpdated'), description: t('security.passwordUpdatedDescription') });
      form.reset();
    } catch (e: any) {
      toast({ variant: 'destructive', title: t('security.updateFailed'), description: e?.message || t('security.updateFailedDescription') });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('security.currentPassword')}</FormLabel>
              <FormControl>
                <PasswordInput placeholder={t('security.currentPasswordPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('security.newPassword')}</FormLabel>
              <FormControl>
                <PasswordInput placeholder={t('security.newPasswordPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('security.confirmPassword')}</FormLabel>
              <FormControl>
                <PasswordInput placeholder={t('security.confirmPasswordPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full md:w-auto">
          <Loader2 className="mr-2 h-4 w-4 hidden data-[state=loading]:inline animate-spin" />
          {t('security.updatePassword')}
        </Button>
      </form>
    </Form>
  );
};

export default PractitionerPasswordForm;
